import {useTranslation} from "react-i18next";
import {useEffect, useMemo, useState} from "react";
import {CONTRACTOR_SEARCH_MODEL} from "../Model/Model";
import {UseWebSocket} from "./useWebSocket";
import HandleChange from "../../../../../Helpers/HandleChange";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 26 de noviembre de 2025
 *
 * IncomingContractorsApp manages live contractor attendance, websocket updates,
 * table pagination, filtering, and modal state for check-in, check-out, and logs.
 *
 * @returns {object} Exposes state handlers, memoized lists, modal controls, websocket handlers.
 */


const IncomingContractorsApp = () => {
    const { t } = useTranslation();
    const [isOnline, setIsOnline] = useState(false);
    const [isReload, setIsReload] = useState(0);

    const [activeContractorsOptions, setActiveContractorsOptions] = useState([]);
    const [activeContractors, setActiveContractors] = useState([]);
    const [employeeInside, setEmployeeInside] = useState([]);
    const [employeeOutside, setEmployeeOutside] = useState([]);
    const [equipmentInside, setEquipmentInside] = useState([]);
    const [equipmentOutside, setEquipmentOutside] = useState([]);
    const [item, setItem] = useState({...CONTRACTOR_SEARCH_MODEL});
    const [search, setSearch] = useState('');
    const [ showCheckInModal, setShowCheckInModal] = useState(false);
    const [ showCheckOutModal, setShowCheckOutModal] = useState(false);
    const [ showLogModal, setShowLogModal] = useState(false);

    const [itemSelected, setItemSelected] = useState(null);
    const [outLog, setOutLog] = useState(null);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    /**
     * WebSocket connection and auto-reconnect handler.
     */
    const { reconnectWebSocket } = UseWebSocket(
        {
            item,
            isOnline,
            setIsOnline,
            setActiveContractorsOptions,
            setActiveContractors,
            setEmployeeInside,
            setEmployeeOutside,
            setEquipmentInside,
            setEquipmentOutside
        }
    );

    const filterBySearch = (list, search) => {
        if (!search) return list;

        const lowerSearch = search.toLowerCase();

        let coincidences = list.filter(e => {
            const companyName = `${e?.companyName || ''}`.toLowerCase();
            const employeeNames = e?.employees?.map(i => `${i?.name || ''}`.toLowerCase()) || [];
            const guardFullName = `${e?.guardFullName || ''}`.toLowerCase();
            const userFullName = `${e?.userFullName || ''}`.toLowerCase();
            const equipmentCode = `${e?.code || ''}`.toLowerCase();
            const equipmentName = `${e?.name || ''}`.toLowerCase();

            return (
                companyName.includes(lowerSearch) ||
                employeeNames?.filter(name => name.includes(lowerSearch))?.length > 0 ||
                guardFullName.includes(lowerSearch) ||
                userFullName.includes(lowerSearch) ||
                equipmentCode.includes(lowerSearch) ||
                equipmentName.includes(lowerSearch)
            );
        });

        if(coincidences?.employees?.length > 0){
            coincidences.employees = coincidences.employees.filter(e => {
                const userFullName = `${e?.name || ''}`.toLowerCase();
                return userFullName.includes(lowerSearch);
            });
        }
        return coincidences;
    };

    /**
     * Memoized filtered lists based on current search value.
     * Prevents unnecessary recalculation when the search or the source list hasn't changed.
     */
    const withSearchActiveContractors = useMemo(
        () => filterBySearch(activeContractors, search),
        [search, activeContractors]
    );

    const withSearchEmployeeInside = useMemo(
        () => filterBySearch(employeeInside, search),
        [search, employeeInside]
    );

    const withSearchEmployeeOutside = useMemo(
        () => filterBySearch(employeeOutside, search),
        [search, employeeOutside]
    );

    const withSearchEquipmentInside = useMemo(
        () => filterBySearch(equipmentInside, search),
        [search, equipmentInside]
    );

    const withSearchEquipmentOutside = useMemo(
        () => filterBySearch(equipmentOutside, search),
        [search, equipmentOutside]
    );

    /**
     * Resets reload counter when the websocket connection is online.
     */
    useEffect(() => {
        if(isOnline){
            setIsReload(0);
        }
    }, [isOnline]);

    /**
     * Triggers websocket reconnection when reload counter increments.
     */
    useEffect(() => {
        if(isReload && isReload > 0 ){
            reconnectWebSocket();
        }
    }, [isReload]);

    return {
        isOnline,
        setIsReload,
        item,
        search,
        setSearch,
        t,
        withSearchActiveContractors,
        withSearchEmployeeInside,
        withSearchEmployeeOutside,
        withSearchEquipmentInside,
        withSearchEquipmentOutside,
        showCheckInModal,
        setShowCheckInModal,
        showCheckOutModal,
        setShowCheckOutModal,
        showLogModal,
        setShowLogModal,
        itemSelected,
        setItemSelected,
        outLog,
        setOutLog,
        handleChange,
        activeContractorsOptions
    };
};
export default IncomingContractorsApp;
